import { DownOutlined } from '@ant-design/icons'
import { Modal, Popover, Tooltip } from 'antd'
import classNames from 'classnames'
import { type ReactNode, useState } from 'react'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomCheckbox from '@/components/Atoms/AtomCheckbox/AtomCheckbox'
import Loader from '@/components/Atoms/Loader/Loader'
import { useFeedPriceSegments } from '@/hooks/search/useFeedPriceSegments'
import { useUserInfoStore } from '@/stores/user/userInfoStore'

import cn from './PriceSegmentsPicker.module.scss'
import {
  CONTEXT_SEGMENT_NOTE,
  getMaxSelectableHint,
  PRICE_SEGMENTS_UNAVAILABLE_TEXTS,
  PROCUREMENT_PARTNER_HINT,
} from './priceSegmentsTexts'
import { usePriceSegmentsPicker } from './usePriceSegmentsPicker'

type PriceSegmentsPickerProps = {
  label: ReactNode
  className?: string
}

function PriceSegmentsPicker({ label, className }: PriceSegmentsPickerProps) {
  const [open, setOpen] = useState(false)
  const [draftIds, setDraftIds] = useState<string[]>([])
  const [modal, contextHolder] = Modal.useModal()
  const picker = usePriceSegmentsPicker(modal)
  const selected = useFeedPriceSegments()
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const { options } = picker
  const isLimitReached =
    options !== null && draftIds.length >= options.maxSelectable

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) return
    setDraftIds(selected.map(({ id }) => id))
    void picker.loadOptions()
  }

  const toggleSegment = (id: string, checked: boolean) =>
    setDraftIds((ids) =>
      checked ? [...ids, id] : ids.filter((draftId) => draftId !== id)
    )

  const handleShow = () => {
    picker.show(
      (options?.segments ?? []).filter((segment) =>
        draftIds.includes(segment.id)
      )
    )
    setOpen(false)
  }

  const handleReset = () => {
    picker.reset()
    setDraftIds([])
    setOpen(false)
  }

  const renderList = () => {
    if (picker.loading) return <Loader size={24} />
    if (picker.loadError || !options)
      return (
        <p className={cn.message}>
          Не удалось загрузить сегменты.{' '}
          <button
            type="button"
            className={cn.link}
            onClick={() => void picker.loadOptions()}
          >
            Повторить
          </button>
        </p>
      )
    if (!options.canSelect)
      return (
        <p className={cn.message}>
          {options.unavailableReason
            ? PRICE_SEGMENTS_UNAVAILABLE_TEXTS[options.unavailableReason]
            : null}
        </p>
      )

    return (
      <ul className={cn.list} aria-label="Сегменты для сравнения цен">
        {options.segments.map((segment) => {
          if (segment.isContext)
            return (
              <li key={segment.id}>
                <AtomCheckbox
                  checked
                  disabled
                  onChange={() => undefined}
                  isOrganisationSelected={isOrganisationSelected}
                >
                  {segment.description}{' '}
                  <span className={cn.note}>{CONTEXT_SEGMENT_NOTE}</span>
                </AtomCheckbox>
              </li>
            )
          const checked = draftIds.includes(segment.id)
          const disabled = !checked && isLimitReached
          return (
            <li key={segment.id}>
              <Tooltip
                title={
                  disabled ? getMaxSelectableHint(options.maxSelectable) : null
                }
              >
                <span>
                  <AtomCheckbox
                    checked={checked}
                    disabled={disabled}
                    onChange={(nextChecked) =>
                      toggleSegment(segment.id, nextChecked)
                    }
                    isOrganisationSelected={isOrganisationSelected}
                  >
                    {segment.description}
                  </AtomCheckbox>
                </span>
              </Tooltip>
            </li>
          )
        })}
      </ul>
    )
  }

  const content = (
    <div className={cn.panel}>
      {renderList()}
      {options?.scenario === 'PROCUREMENT' && (
        <p className={cn.hint}>{PROCUREMENT_PARTNER_HINT}</p>
      )}
      <div className={cn.actions}>
        <AtomButton variant="secondary" size="small" onClick={handleReset}>
          Сбросить
        </AtomButton>
        <AtomButton
          size="small"
          disabled={!options?.canSelect}
          onClick={handleShow}
        >
          Показать
        </AtomButton>
      </div>
    </div>
  )

  return (
    <>
      {contextHolder}
      <Popover
        open={open}
        onOpenChange={handleOpenChange}
        trigger="click"
        placement="bottomLeft"
        content={content}
      >
        <button
          type="button"
          className={classNames(
            cn.trigger,
            isOrganisationSelected && cn.triggerSelected,
            className
          )}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(event) => event.stopPropagation()}
        >
          {label}
          <DownOutlined className={cn.arrow} aria-hidden />
        </button>
      </Popover>
    </>
  )
}

export default PriceSegmentsPicker
